import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { z } from 'zod';
import { useRetirarPaquete } from '@/lib/hooks/use-paquetes';
import { ApiRequestError } from '@/lib/api/errors';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

/** Flat form schema used by react-hook-form. Validation of pin vs sinPin is done manually. */
const RetirarFormSchema = z.object({
  retiradoPorNombre: z.string().min(1, 'Requerido').max(200),
  pin: z.string().optional(),
  razon: z.string().optional(),
});

type RetirarFormValues = z.infer<typeof RetirarFormSchema>;

interface RetirarDialogProps {
  paqueteId: string;
  destinatarioNombre: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RetirarDialog({
  paqueteId,
  destinatarioNombre,
  open,
  onOpenChange,
}: RetirarDialogProps) {
  const mutation = useRetirarPaquete();
  const [sinPin, setSinPin] = useState(false);
  const [pinError, setPinError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<RetirarFormValues>({
    defaultValues: {
      retiradoPorNombre: '',
      pin: '',
      razon: '',
    },
  });

  function handleToggleSinPin() {
    setSinPin((prev) => !prev);
    setPinError(null);
  }

  function handleClose(open: boolean) {
    if (!open) {
      reset();
      setSinPin(false);
      setPinError(null);
    }
    onOpenChange(open);
  }

  async function onSubmit(values: RetirarFormValues) {
    setPinError(null);

    if (!sinPin) {
      const pin = values.pin ?? '';
      if (!/^\d{6}$/.test(pin)) {
        setPinError('El PIN debe ser 6 digitos numericos');
        return;
      }
      try {
        await mutation.mutateAsync({
          id: paqueteId,
          body: { retiradoPorNombre: values.retiradoPorNombre, pin },
        });
        toast.success('Paquete retirado', {
          description: `${destinatarioNombre} recibio su paquete.`,
        });
        handleClose(false);
      } catch (err) {
        if (
          err instanceof ApiRequestError &&
          err.body.type === 'urn:condo:invalid-pin'
        ) {
          setPinError('PIN invalido, intenta de nuevo');
        } else {
          toast.error('No se pudo registrar el retiro');
        }
      }
    } else {
      const razon = values.razon ?? '';
      if (razon.trim().length === 0) {
        return; // form validation handles this via required below
      }
      try {
        await mutation.mutateAsync({
          id: paqueteId,
          body: { retiradoPorNombre: values.retiradoPorNombre, sinPin: { razon } },
        });
        toast.success('Paquete retirado sin PIN', {
          description: `${destinatarioNombre} recibio su paquete.`,
        });
        handleClose(false);
      } catch {
        toast.error('No se pudo registrar el retiro');
      }
    }
  }

  const nombreValue = watch('retiradoPorNombre');
  const razonValue = watch('razon');

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Registrar retiro</DialogTitle>
          <DialogDescription>
            Confirma quien retira el paquete de {destinatarioNombre}.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          {/* Nombre del retirante */}
          <div>
            <Label htmlFor="ret-nombre">Nombre de quien retira</Label>
            <Input
              id="ret-nombre"
              className="mt-1.5"
              placeholder="Nombre completo"
              {...register('retiradoPorNombre', { required: 'Requerido' })}
            />
            {errors.retiradoPorNombre && (
              <p className="mt-1 text-xs text-[var(--color-danger)]">
                {errors.retiradoPorNombre.message}
              </p>
            )}
          </div>

          {/* PIN / Sin PIN toggle */}
          {!sinPin ? (
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="ret-pin">PIN de retiro (6 digitos)</Label>
                <button
                  type="button"
                  onClick={handleToggleSinPin}
                  className="text-xs text-[var(--color-primary)] underline-offset-2 hover:underline"
                >
                  Sin PIN
                </button>
              </div>
              <Input
                id="ret-pin"
                inputMode="numeric"
                maxLength={6}
                className="mt-1.5 font-mono tracking-widest"
                placeholder="······"
                {...register('pin')}
              />
              {pinError && (
                <p className="mt-1 text-xs text-[var(--color-danger)]">{pinError}</p>
              )}
            </div>
          ) : (
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="ret-razon">Razon (sin PIN)</Label>
                <button
                  type="button"
                  onClick={handleToggleSinPin}
                  className="text-xs text-[var(--color-primary)] underline-offset-2 hover:underline"
                >
                  Usar PIN
                </button>
              </div>
              <textarea
                id="ret-razon"
                rows={3}
                className="mt-1.5 flex w-full rounded border border-[var(--color-hairline-strong)] bg-[var(--color-paper)] px-3 py-2 text-sm text-[var(--color-ink)] placeholder:text-[var(--color-ink-faint)] transition-[border-color,box-shadow] duration-150 ease-out focus:outline-none focus:border-[var(--color-primary)] focus:shadow-[0_0_0_3px_color-mix(in_oklch,var(--color-primary)_20%,transparent)]"
                placeholder="Motivo por el que se omite el PIN"
                {...register('razon', { required: sinPin ? 'Requerido' : false })}
              />
              {errors.razon && (
                <p className="mt-1 text-xs text-[var(--color-danger)]">
                  {errors.razon.message}
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => handleClose(false)}
              disabled={mutation.isPending}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={
                mutation.isPending ||
                !nombreValue ||
                (!sinPin ? false : !razonValue?.trim())
              }
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Registrando…
                </>
              ) : (
                'Confirmar retiro'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
